/**
 * Provider listing sync — maps a simplified "My Services" form into the
 * existing Sharetribe ownListings + availabilityExceptions APIs.
 *
 * Pure helpers are exported for unit tests; syncProviderServices orchestrates
 * sequential SDK calls (create/update/publish/close/open + exception sync).
 */

import { types as sdkTypes } from './sdkLoader';
import { isFieldForCategory, isFieldForListingType } from './fieldHelpers';
import {
  LISTING_STATE_CLOSED,
  LISTING_STATE_DRAFT,
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_PUBLISHED,
} from './types';

const { Money, UUID } = sdkTypes;

export const SELL_SERVICES_LISTING_TYPE = 'sell-services';
export const SELL_SERVICES_PROCESS_ALIAS = 'default-booking/release-1';
export const SELL_SERVICES_UNIT_TYPE = 'hour';
export const DEFAULT_LISTING_PRICE_SUBUNITS = 10000;
export const TITLE_MAX_LENGTH = 60;
export const DEFAULT_SERVICE_RADIUS_KM = 20;
export const CATEGORY_PREFIX = 'categoryLevel';

export const ACTIVE_LISTING_STATES = [LISTING_STATE_PUBLISHED, LISTING_STATE_PENDING_APPROVAL];
export const CLOSED_LISTING_STATE = LISTING_STATE_CLOSED;
export const DRAFT_LISTING_STATE = LISTING_STATE_DRAFT;

/**
 * Truncate a string to maxLength without cutting mid-word when possible.
 * @param {string} title
 * @param {number} [maxLength]
 * @returns {string}
 */
export const truncateTitle = (title, maxLength = TITLE_MAX_LENGTH) => {
  const trimmed = (title || '').trim();
  if (trimmed.length <= maxLength) {
    return trimmed;
  }
  return trimmed.slice(0, maxLength - 1).trimEnd() + '…';
};

/**
 * Auto-generate listing title from provider display name and category name.
 * @param {string} displayName
 * @param {string} categoryName
 * @returns {string}
 */
export const buildListingTitle = (displayName, categoryName) => {
  const name = (displayName || '').trim() || 'Provider';
  const category = (categoryName || '').trim() || 'Service';
  return truncateTitle(`${name} — ${category}`);
};

/**
 * Resolve level-1 category id from a listing's publicData.
 * @param {Object} listing
 * @param {string} [prefix]
 * @returns {string|null}
 */
export const getListingCategoryLevel1 = (listing, prefix = CATEGORY_PREFIX) => {
  return listing?.attributes?.publicData?.[`${prefix}1`] || null;
};

/**
 * Find an existing listing for a level-1 category (any state).
 * Prefers active, then closed, then draft, then any other.
 * @param {Array} listings
 * @param {string} categoryId
 * @param {string} [prefix]
 * @returns {Object|null}
 */
export const findListingByCategory = (listings = [], categoryId, prefix = CATEGORY_PREFIX) => {
  if (!categoryId) {
    return null;
  }
  const matches = listings.filter(l => getListingCategoryLevel1(l, prefix) === categoryId);
  if (matches.length === 0) {
    return null;
  }
  const byState = state => matches.find(l => l.attributes?.state === state);
  return (
    byState(LISTING_STATE_PUBLISHED) ||
    byState(LISTING_STATE_PENDING_APPROVAL) ||
    byState(LISTING_STATE_CLOSED) ||
    byState(LISTING_STATE_DRAFT) ||
    matches[0]
  );
};

/**
 * Level-1 category ids that currently have an active listing.
 * @param {Array} listings
 * @param {string} [prefix]
 * @returns {string[]}
 */
export const getActiveCategoryIds = (listings = [], prefix = CATEGORY_PREFIX) => {
  return listings
    .filter(l => ACTIVE_LISTING_STATES.includes(l.attributes?.state))
    .map(l => getListingCategoryLevel1(l, prefix))
    .filter(Boolean);
};

/**
 * Count distinct published service categories (bookable listings).
 * Pending/draft/closed do not count as bookable.
 * @param {Array} listings
 * @param {string} [prefix]
 * @returns {number}
 */
export const countPublishedServiceListings = (listings = [], prefix = CATEGORY_PREFIX) => {
  const publishedCategoryIds = listings
    .filter(l => l.attributes?.state === LISTING_STATE_PUBLISHED)
    .map(l => getListingCategoryLevel1(l, prefix))
    .filter(Boolean);
  return new Set(publishedCategoryIds).size;
};

/**
 * Pick canonical listing for pre-filling the form.
 * Prefer first published; else most recently updated with location data.
 * @param {Array} listings
 * @returns {Object|null}
 */
export const pickCanonicalListing = (listings = []) => {
  if (!listings.length) {
    return null;
  }
  const published = listings.filter(l => l.attributes?.state === LISTING_STATE_PUBLISHED);
  if (published.length > 0) {
    return published[0];
  }
  const withLocation = listings
    .filter(l => l.attributes?.publicData?.location?.address && l.attributes?.geolocation)
    .slice()
    .sort((a, b) => {
      const aTime = new Date(a.attributes?.updatedAt || 0).getTime();
      const bTime = new Date(b.attributes?.updatedAt || 0).getTime();
      return bTime - aTime;
    });
  return withLocation[0] || listings[0];
};

/**
 * Detect whether other listings differ from the canonical location/availability.
 * Used to show an info banner that save will overwrite.
 * @param {Array} listings
 * @param {Object|null} canonical
 * @returns {boolean}
 */
export const hasMismatchedSharedFields = (listings = [], canonical = null) => {
  if (!canonical || listings.length < 2) {
    return false;
  }
  const canonLoc = JSON.stringify(canonical.attributes?.publicData?.location || null);
  const canonGeo = JSON.stringify(canonical.attributes?.geolocation || null);
  const canonPlan = JSON.stringify(canonical.attributes?.availabilityPlan || null);

  return listings.some(l => {
    if (l.id?.uuid === canonical.id?.uuid) {
      return false;
    }
    if (!ACTIVE_LISTING_STATES.includes(l.attributes?.state)) {
      return false;
    }
    const loc = JSON.stringify(l.attributes?.publicData?.location || null);
    const geo = JSON.stringify(l.attributes?.geolocation || null);
    const plan = JSON.stringify(l.attributes?.availabilityPlan || null);
    return loc !== canonLoc || geo !== canonGeo || plan !== canonPlan;
  });
};

/**
 * Build Sharetribe listing attributes payload for create/update.
 * @param {Object} params
 * @returns {Object}
 */
export const buildListingPayload = ({
  categoryId,
  categoryName,
  displayName,
  bio,
  location,
  geolocation,
  availabilityPlan,
  currency = 'AUD',
  existingPublicData = {},
}) => {
  const title = buildListingTitle(displayName, categoryName);
  const description = typeof bio === 'string' ? bio : '';
  const address = location?.address || '';
  const building = location?.building || '';
  const radius =
    location?.radius != null && location.radius !== ''
      ? Number(location.radius)
      : DEFAULT_SERVICE_RADIUS_KM;

  const publicData = {
    ...existingPublicData,
    listingType: SELL_SERVICES_LISTING_TYPE,
    transactionProcessAlias: SELL_SERVICES_PROCESS_ALIAS,
    unitType: SELL_SERVICES_UNIT_TYPE,
    // Clear deeper category levels — level-1 only
    categoryLevel1: categoryId,
    categoryLevel2: null,
    categoryLevel3: null,
    location: { address, building, radius },
  };

  const payload = {
    title,
    description,
    price: new Money(DEFAULT_LISTING_PRICE_SUBUNITS, currency),
    publicData,
  };

  if (geolocation) {
    payload.geolocation = geolocation;
  }
  if (availabilityPlan) {
    payload.availabilityPlan = availabilityPlan;
  }

  return payload;
};

/**
 * Decide which SDK action chain is needed for one category.
 * @param {Object|null} listing
 * @param {boolean} selected
 * @returns {'update'|'update-open'|'update-publish'|'create-publish'|'close'|'noop'}
 */
export const resolveCategoryAction = (listing, selected) => {
  if (!selected) {
    if (listing && ACTIVE_LISTING_STATES.includes(listing.attributes?.state)) {
      return 'close';
    }
    return 'noop';
  }
  if (!listing) {
    return 'create-publish';
  }
  const state = listing.attributes?.state;
  if (state === CLOSED_LISTING_STATE) {
    return 'update-open';
  }
  if (state === DRAFT_LISTING_STATE) {
    return 'update-publish';
  }
  if (ACTIVE_LISTING_STATES.includes(state)) {
    return 'update';
  }
  return 'create-publish';
};

/**
 * Plan sync operations for all level-1 categories.
 * Creates/updates selected first; closes unselected after.
 * @param {Object} params
 * @returns {Array<{categoryId: string, categoryName: string, listing: Object|null, action: string}>}
 */
export const planSyncOperations = ({
  selectedCategoryIds = [],
  categories = [],
  userListings = [],
}) => {
  const selectedSet = new Set(selectedCategoryIds);
  const level1 = categories.map(c => ({ id: c.id, name: c.name }));

  const activateOps = level1
    .filter(c => selectedSet.has(c.id))
    .map(c => {
      const listing = findListingByCategory(userListings, c.id);
      return {
        categoryId: c.id,
        categoryName: c.name,
        listing,
        action: resolveCategoryAction(listing, true),
      };
    });

  const closeOps = level1
    .filter(c => !selectedSet.has(c.id))
    .map(c => {
      const listing = findListingByCategory(userListings, c.id);
      return {
        categoryId: c.id,
        categoryName: c.name,
        listing,
        action: resolveCategoryAction(listing, false),
      };
    })
    .filter(op => op.action === 'close');

  return [...activateOps, ...closeOps];
};

/**
 * Runtime audit: required listing fields limited to categories (from Console config).
 * Local repo listingFields are empty; hosted assets may still define some.
 * @param {Array} listingFields
 * @param {string} [listingType]
 * @returns {Array}
 */
export const getRequiredCategorySpecificListingFields = (
  listingFields = [],
  listingType = SELL_SERVICES_LISTING_TYPE
) => {
  return listingFields.filter(field => {
    const isRequired = field?.saveConfig?.isRequired;
    const limitedToCategories = field?.categoryConfig?.limitToCategoryIds;
    const appliesToType = isFieldForListingType(listingType, field);
    return isRequired && limitedToCategories && appliesToType;
  });
};

/**
 * Categories that would hit required category-specific fields (warning for UI).
 * @param {Array} selectedCategoryIds
 * @param {Array} listingFields
 * @returns {string[]}
 */
export const getCategoriesWithRequiredExtraFields = (
  selectedCategoryIds = [],
  listingFields = []
) => {
  const required = getRequiredCategorySpecificListingFields(listingFields);
  if (required.length === 0) {
    return [];
  }
  return selectedCategoryIds.filter(categoryId =>
    required.some(field => isFieldForCategory([categoryId], field))
  );
};

/**
 * Normalize exception attributes for recreation on another listing.
 * @param {Object} exception
 * @returns {{start: Date|string, end: Date|string, seats: number}}
 */
export const normalizeExceptionForCreate = exception => {
  const attrs = exception?.attributes || exception || {};
  return {
    start: attrs.start,
    end: attrs.end,
    seats: attrs.seats != null ? attrs.seats : 0,
  };
};

/**
 * Sync availability exceptions for one listing: delete all existing, recreate from canonical.
 * @param {Object} sdk
 * @param {Object} listingId - UUID object or {uuid}
 * @param {Array} canonicalExceptions
 */
export const syncAvailabilityExceptionsForListing = async (
  sdk,
  listingId,
  canonicalExceptions = []
) => {
  const id = listingId?.uuid ? listingId : new UUID(listingId);
  const queryResponse = await sdk.availabilityExceptions.query({
    listingId: id,
  });
  const existing = queryResponse?.data?.data || [];

  for (const ex of existing) {
    await sdk.availabilityExceptions.delete({ id: ex.id });
  }

  for (const ex of canonicalExceptions) {
    const normalized = normalizeExceptionForCreate(ex);
    if (!normalized.start || !normalized.end) {
      continue;
    }
    await sdk.availabilityExceptions.create({
      listingId: id,
      start: normalized.start,
      end: normalized.end,
      seats: normalized.seats,
    });
  }
};

const listingIdFromResponse = response => response?.data?.data?.id;

/**
 * Execute one category operation against the SDK.
 * @returns {Promise<{categoryId: string, status: 'ok'|'error'|'skipped', listingId?: Object, error?: Error}>}
 */
export const executeCategoryOperation = async (sdk, op, sharedPayload, currency) => {
  const { categoryId, categoryName, listing, action } = op;
  if (action === 'noop') {
    return { categoryId, status: 'skipped' };
  }

  const payload = buildListingPayload({
    ...sharedPayload,
    categoryId,
    categoryName,
    currency,
    existingPublicData: listing?.attributes?.publicData || {},
  });

  try {
    if (action === 'close') {
      await sdk.ownListings.close({ id: listing.id }, { expand: true });
      return { categoryId, status: 'ok', listingId: listing.id, action };
    }

    if (action === 'update') {
      await sdk.ownListings.update({ id: listing.id, ...payload }, { expand: true });
      return { categoryId, status: 'ok', listingId: listing.id, action };
    }

    if (action === 'update-open') {
      await sdk.ownListings.update({ id: listing.id, ...payload }, { expand: true });
      await sdk.ownListings.open({ id: listing.id }, { expand: true });
      return { categoryId, status: 'ok', listingId: listing.id, action };
    }

    if (action === 'update-publish') {
      await sdk.ownListings.update({ id: listing.id, ...payload }, { expand: true });
      await sdk.ownListings.publishDraft({ id: listing.id }, { expand: true });
      return { categoryId, status: 'ok', listingId: listing.id, action };
    }

    if (action === 'create-publish') {
      const createResponse = await sdk.ownListings.createDraft(payload, { expand: true });
      const newId = listingIdFromResponse(createResponse);
      await sdk.ownListings.publishDraft({ id: newId }, { expand: true });
      return { categoryId, status: 'ok', listingId: newId, action };
    }

    return { categoryId, status: 'skipped' };
  } catch (error) {
    return { categoryId, status: 'error', error, action };
  }
};

/**
 * Full sync: activate/update selected services, close unselected, then sync exceptions
 * onto all successfully active listings.
 *
 * @param {Object} params
 * @param {Object} params.sdk
 * @param {string[]} params.selectedCategoryIds
 * @param {Array} params.categories - level-1 category configs {id, name}
 * @param {Array} params.userListings
 * @param {Object} params.sharedFields - { displayName, bio, location, geolocation, availabilityPlan }
 * @param {Array} params.canonicalExceptions
 * @param {string} [params.currency]
 * @returns {Promise<{results: Array, activeListingIds: Array, exceptionSyncErrors: Array}>}
 */
export const syncProviderServices = async ({
  sdk,
  selectedCategoryIds,
  categories,
  userListings,
  sharedFields,
  canonicalExceptions = [],
  currency = 'AUD',
}) => {
  const operations = planSyncOperations({
    selectedCategoryIds,
    categories,
    userListings,
  });

  const results = [];
  const activeListingIds = [];

  // Process activate/update first, then closes (planSyncOperations already orders this way)
  for (const op of operations) {
    const result = await executeCategoryOperation(sdk, op, sharedFields, currency);
    results.push(result);

    if (
      result.status === 'ok' &&
      result.listingId &&
      result.action !== 'close'
    ) {
      activeListingIds.push(result.listingId);
    }
  }

  // Also include already-active listings that were updated (or pending) and selected
  // Re-derive from successful non-close ops above — activeListingIds is complete.

  const exceptionSyncErrors = [];
  for (const listingId of activeListingIds) {
    try {
      await syncAvailabilityExceptionsForListing(sdk, listingId, canonicalExceptions);
    } catch (error) {
      exceptionSyncErrors.push({ listingId, error });
    }
  }

  return { results, activeListingIds, exceptionSyncErrors };
};
